import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useAuth } from "context/AuthContext";

/**
 * Shared mutation helper for the back-office admin screens.
 * Guards against duplicate submits, clears the session on 401 and
 * surfaces the gateway error message for the UI banner.
 */
export default function useAdminMutation(refresh) {
  const { token, logout } = useAuth();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const lock = useRef(false);
  const currentToken = useRef(token);
  currentToken.current = token;
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  async function submit(api, path, options, onSuccess, successMessage) {
    if (lock.current) return;
    lock.current = true;
    setBusy(true);
    setError(null);
    try {
      const data = await api(path, { ...options, token });
      if (!mounted.current || currentToken.current !== token) return;
      setResult(data);
      toast.success(successMessage || "Action completed successfully.");
      onSuccess?.(data);
      refresh?.();
    } catch (err) {
      if (!mounted.current || currentToken.current !== token) return;
      setError(err.message);
      if (err.status === 401) logout();
      if (err.status === 400 || err.status === 409) refresh?.();
    } finally {
      lock.current = false;
      if (mounted.current) setBusy(false);
    }
  }

  return {
    busy,
    error,
    result,
    submit,
    reset: () => {
      setError(null);
      setResult(null);
    },
  };
}
